import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLogs } from "../context/LogContext"; // Custom context for logging
import "./Register.css";
import { useEncryptDecrypt } from "../utils/EncryptedDecrypted";
import generateHMAC from "../utils/HMAC";
import generateDeviceId from "../utils/GenerateKey";
import deviceDataFun from "../utils/DeviceData";
import { api } from "../utils/api";

const Register = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // const [phoneNumber, setPhoneNumber] = useState("");
  // const [emailHash, setEmailHash] = useState("");
  // const [phoneNumberHash, setPhoneNumberHash] = useState("");
  const [error, setError] = useState("");
  const { addLog } = useLogs();
  const { encrypt } = useEncryptDecrypt(); // Use the custom hook
  const navigate = useNavigate();
  const location = useLocation();
  const phoneNumber = location.state?.phoneNumber;
  useEffect(() => {
    if (!phoneNumber) {
      // If phoneNumber is not available, redirect to home
      navigate("/");
    }
  }, [phoneNumber, navigate]);
  const handleRegister = async () => {
    addLog(`Registration form submitted`);
    if (!/\S+@\S+\.\S+/.test(email)) {
      const errMsg = "Invalid email format.";
      addLog(`Validation Error: ${errMsg}`);
      setError(errMsg);
      return;
    }

    // Get BCH registration data from localStorage (set by Scan component)
    let bchData = null;
    try {
      const storedBchData = localStorage.getItem("bchRegistrationData");
      if (storedBchData) {
        bchData = JSON.parse(storedBchData);

        // Handle both old format (single object) and new format (array of objects)
        if (Array.isArray(bchData)) {
          // New format: array of objects, each with ecc, hashHex, r, helper
          addLog(
            `BCH registration data loaded: ${bchData.length} objects in array`
          );
          if (bchData.length > 0) {
            const firstObj = bchData[0];
            addLog(
              `Each object contains: ecc(${
                firstObj.ecc?.length || 0
              }), hashHex(${firstObj.hashHex?.length || 0}), r(${
                firstObj.r?.length || 0
              }), helper(${firstObj.helper?.length || 0})`
            );
          }
        } else if (bchData.r && typeof bchData.r === "string") {
          // Old format: single registration data object
          addLog(
            `BCH registration data loaded (old format): r(${
              bchData.r?.length || 0
            } bits), helper(${bchData.helper?.length || 0} bits), ecc(${
              bchData.ecc?.length || 0
            } bits)`
          );
        } else {
          addLog(`BCH registration data loaded: Unknown format`);
        }
      } else {
        addLog(
          `Warning: No BCH registration data found. User may need to complete face scan first.`
        );
      }
    } catch (err) {
      console.error("Failed to load BCH data:", err);
      addLog(`Error loading BCH data: ${err.message}`);
    }

    const fcmToken = "dsd";
    const encryptedFirstName = encrypt(firstName);
    const encryptedLastName = encrypt(lastName);
    const encryptedEmail = encrypt(email);
    const encryptedPhone = encrypt(phoneNumber);
    const emailHash = await generateHMAC(email);
    console.log("emailHash", emailHash);
    const phoneNumberHash = await generateHMAC(phoneNumber);
    const deviceKeygen = await generateDeviceId();
    // const deviceKeygen = "wait generateDeviceId()";
    const deviceKey = encrypt(deviceKeygen);
    const deviceKeyHash = await generateHMAC(deviceKeygen);
    const deviceData = deviceDataFun();
    // console.log("deviceData", deviceData);
    const encryptedData = {
      firstName: encryptedFirstName,
      lastName: encryptedLastName,
      email: encryptedEmail,
      emailHash: emailHash,
      phoneNumber: encryptedPhone,
      phoneNumberHash: phoneNumberHash,
      deviceKey,
      deviceKeyHash,
      deviceName: deviceData.deviceName,
      deviceData,
      fcmToken,
    };
    console.log("encryptedData", encryptedData);
    addLog(
      `API Request Body: 
          { 
            firstName: ${encryptedFirstName}, 
            lastName: ${encryptedLastName}, 
            email: ${encryptedEmail}, 
            emailHash: ${emailHash}, 
            phoneNumber: ${encryptedPhone}, 
            phoneNumberHash: ${phoneNumberHash}, 
            phoneNumber: ${phoneNumber}, 
            browserKey: ${deviceKey}, 
            browserKeyHash: ${deviceKeyHash}, 
            deviceName:${deviceData.deviceName},
            deviceDetails:{
                              Browser Name: ${deviceData.browser},
                              Browser Version: ${deviceData.browserVersion},
                              Operation system : ${deviceData.osName},
                              Screen Width : ${deviceData.screenWidth},
                              Screen Height : ${deviceData.screenHeight},
                           }
          }`
    );

    if (!firstName || !lastName || !email || !phoneNumber) {
      const errMsg = "All fields are required.";
      addLog(`Validation Error: ${errMsg}`);
      setError(errMsg);
      return;
    }

    try {
      // Assuming registerApi is available for registration, replace loginApi with registerApi
      const response = await api.post(`/users/user-register`, encryptedData);

      // Axios response shape is usually { data: { ... } }, and many APIs nest as { data: { data: ... } }.
      // Guard hard to avoid crashes when backend returns an unexpected shape or empty body.
      const payload = response?.data?.data ?? response?.data;
      if (!payload) {
        throw new Error("Registration succeeded but response payload was empty.");
      }

      addLog(`API Response: Successfully registered user`);
      localStorage.setItem("userData", JSON.stringify(payload));

      // Try a few common shapes for the new user id (keep as plain string).
      const newUserId =
        payload?.newUser?._id ??
        payload?.user?._id ??
        payload?.userId ??
        payload?._id;
      if (newUserId) {
        localStorage.setItem("userId", String(newUserId));
      } else {
        addLog(`Warning: userId missing in registration response payload`);
      }

      // Set access flag for /scan so the Guard allows it
      sessionStorage.setItem("canAccessScan", "true");
      navigate("/scan");
    } catch (err) {
      // Axios error: err.response?.data?.message; fallback to generic message
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Registration failed. Please try again.";
      addLog(`API Error: ${message}`);
      setError(message);
    }
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  useEffect(() => {}, [error]);

  return (
    <div className="register-form">
      <h2>Register</h2>

      <div className="name-fields">
        <input
          type="text"
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <input
          type="text"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </div>

      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      {/* <input
        type="text"
        placeholder="Phone Number"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
      /> */}

      <button onClick={handleRegister}>Next</button>

      {error && <p className="error">{error}</p>}
    </div>
  );
};

export default Register;
